import { useState, useCallback } from 'react';
import { pdfjs } from 'react-pdf';

// Use pdfjs from react-pdf to ensure version compatibility
const pdfjsLib = pdfjs;

// Configure PDF.js worker
// Use a static path that works in both development and production
// The worker file is copied to public folder during build
// In production, this becomes an absolute path like /pdf.worker.min.mjs
// This avoids issues with dynamic imports and mixed content (HTTP/HTTPS)
if (typeof window !== 'undefined') {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs?v=${pdfjsLib.version}-legacy`;
}

interface UsePdfUtilsResult {
    countPages: (file: File) => Promise<number>;
    isProcessing: boolean;
    error: string | null;
}

/**
 * Hook to handle PDF operations using pdfjs-dist
 * Runs in a worker thread for non-blocking performance
 */
export function usePdfUtils(): UsePdfUtilsResult {
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Count the number of pages in a PDF file
     * @param file The PDF file to analyze
     * @returns The number of pages in the PDF
     */
    const countPages = useCallback(async (file: File): Promise<number> => {
        setIsProcessing(true);
        setError(null);

        try {
            // Convert file to ArrayBuffer
            const arrayBuffer = await file.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            // Load the PDF document
            const loadingTask = pdfjsLib.getDocument({ data: uint8Array });
            const pdf = await loadingTask.promise;

            // Get page count
            const pageCount = pdf.numPages;

            return pageCount;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to count PDF pages';
            setError(message);
            throw new Error(message);
        } finally {
            setIsProcessing(false);
        }
    }, []);

    return {
        countPages,
        isProcessing,
        error
    };
}
